import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Language } from "../types";

const GA_ID = "G-GK0M1FS5RQ";
const STORAGE_KEY = "cookie-consent";

// Carrega o Google Analytics apenas depois de consentimento explícito (RGPD).
function loadAnalytics() {
  if (document.getElementById("ga-script")) return;

  const script = document.createElement("script");
  script.id = "ga-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  function gtag(...args: any[]) {
    w.dataLayer.push(args);
  }
  w.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_ID, { anonymize_ip: true });
}

interface CookieConsentProps {
  lang: Language;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({ lang }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let consent: string | null = null;
    try {
      consent = localStorage.getItem(STORAGE_KEY);
    } catch {
      // localStorage indisponível — mostra o banner por segurança
    }

    if (consent === "accepted") {
      loadAnalytics();
    } else if (consent !== "rejected") {
      setVisible(true);
    }
  }, []);

  const decide = (choice: "accepted" | "rejected") => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // ignore
    }
    if (choice === "accepted") loadAnalytics();
    setVisible(false);
  };

  if (!visible) return null;

  const t =
    lang === "pt"
      ? {
          text: "Utilizamos cookies de análise (Google Analytics) para melhorar o site. Só os ativamos com o seu consentimento.",
          accept: "Aceitar",
          reject: "Recusar",
          policy: "Política de Privacidade",
        }
      : {
          text: "We use analytics cookies (Google Analytics) to improve the site. They are only enabled with your consent.",
          accept: "Accept",
          reject: "Decline",
          policy: "Privacy Policy",
        };

  return (
    <div
      role="dialog"
      aria-label={lang === "pt" ? "Consentimento de cookies" : "Cookie consent"}
      className="fixed bottom-6 left-6 right-6 sm:right-auto z-50 max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 animate-fade-in"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 shrink-0 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
          <Cookie size={18} />
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">
          {t.text}{" "}
          <Link
            to="/legal#privacidade"
            className="font-semibold text-slate-900 underline hover:text-amber-700"
          >
            {t.policy}
          </Link>
        </p>
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => decide("rejected")}
          className="flex-1 py-2.5 rounded-xl text-sm font-bold text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          {t.reject}
        </button>
        <button
          onClick={() => decide("accepted")}
          className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-slate-900 hover:bg-amber-600 transition-colors"
        >
          {t.accept}
        </button>
      </div>
    </div>
  );
};
